import { useCallback } from 'react';
import { useOktaAuth } from '@okta/okta-react';

/**
 * useBearerFetch
 * Fournit une fonction fetch signée avec le token Okta courant.
 * - Ajoute automatiquement Authorization: Bearer <accessToken>
 * - Option retryOn401 pour tenter un renouvellement silencieux (si configuré) puis relancer la requête
 * - Ne lance la requête que si accessToken disponible (sinon lève une Error explicite)
 */
export function useBearerFetch({ retryOn401 = true } = {}) {
  const { authState, oktaAuth } = useOktaAuth();

  const bearerFetch = useCallback(async (input, init = {}) => {
    if (!authState?.isAuthenticated || !authState?.accessToken?.accessToken) {
      throw new Error('Access token not available yet');
    }
    const accessToken = authState.accessToken.accessToken;
    const headers = new Headers(init.headers || {});
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    const response = await fetch(input, { ...init, headers });
    if (response.status === 401 && retryOn401) {
      try {
        await oktaAuth.tokenManager.renew('accessToken');
        const renewed = authState?.accessToken?.accessToken || (await oktaAuth.getAccessToken());
        const retryHeaders = new Headers(init.headers || {});
        retryHeaders.set('Authorization', `Bearer ${renewed}`);
        return fetch(input, { ...init, headers: retryHeaders });
      } catch (e) {
        return response; // retourner l'original si échec renewal
      }
    }
    return response;
  }, [authState, oktaAuth, retryOn401]);

  return bearerFetch;
}

export default useBearerFetch;
